/**
 * HTML Sanitization utility.
 *
 * EVERY use of dangerouslySetInnerHTML MUST go through this function.
 * Uses DOMPurify to strip XSS vectors (script tags, event handlers, javascript: URLs).
 *
 * Usage:
 *   <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(untrustedContent) }} />
 *
 * Closes #713 — wiki article remote-image referrer policy. DOMPurify
 * allows authored content to include <img src="https://..."> from
 * arbitrary remote hosts, which historically leaked tenant page URLs
 * via the Referer header AND let untrusted tracking pixels fire as
 * soon as the article rendered. The afterSanitizeAttributes hook below
 * pins:
 *   - <img>           → referrerpolicy="no-referrer", loading="lazy"
 *   - <a target=_blank> → rel adds "noreferrer noopener"
 *
 * Air-gap compatible — no image proxy, no remote fetches at sanitize
 * time. If we ever ship an image-proxy endpoint, this hook is the
 * natural rewrite point for remote src URLs.
 */
import DOMPurify from 'dompurify';

// Register the hook once at module load. DOMPurify exposes addHook on
// the default export; it dispatches per-node after attribute scrubbing.
// The hook is idempotent: registering it twice would just queue two
// invocations, but our caller imports this module once so we accept
// the simpler shape over a guard ref.
//
// In a Node test environment without JSDOM, the default DOMPurify
// export is the lazy-init stub and `addHook` is not callable until
// first use against a real DOM. Guard with a typeof check so importing
// this module never throws in unit tests that don't exercise
// sanitization. The browser bundle always has addHook available at
// import time, so the production path is unchanged.
if (typeof (DOMPurify as unknown as { addHook?: unknown }).addHook === 'function') {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // Defensive: hooks fire for nodes without tagName too (text nodes,
    // attribute nodes when an attribute is hoisted). Bail unless we have
    // an element to inspect.
    if (!node || typeof (node as Element).tagName !== 'string') return;
    const el = node as Element;
    const tag = el.tagName;

    if (tag === 'IMG') {
      // #713 — block Referer leakage + delay tracking-pixel fetches.
      el.setAttribute('referrerpolicy', 'no-referrer');
      el.setAttribute('loading', 'lazy');
    }

    if (tag === 'A') {
      // #713 cousin — outbound new-tab links must not be open to
      // tabnabbing. Same-tab anchors (no target, or target="_self") are
      // left alone so internal #section links keep working.
      const target = el.getAttribute('target');
      if (target === '_blank') {
        const existingRel = el.getAttribute('rel') ?? '';
        const tokens = new Set(existingRel.split(/\s+/).filter(Boolean));
        tokens.add('noreferrer');
        tokens.add('noopener');
        el.setAttribute('rel', Array.from(tokens).join(' '));
      }
    }
  });
}

/**
 * Sanitize HTML content for safe rendering.
 * Strips all script tags, event handlers, and dangerous attributes.
 * Allows standard formatting tags, links, images, tables.
 */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      // Structure
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div', 'span', 'br', 'hr',
      // Formatting
      'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'mark', 'sub', 'sup', 'small',
      // Lists
      'ul', 'ol', 'li',
      // Links & media
      'a', 'img',
      // Tables
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
      // Code
      'pre', 'code', 'kbd', 'samp', 'var',
      // Blockquote
      'blockquote', 'cite',
      // Task lists (Tiptap)
      'input',
      // Semantic
      'article', 'section', 'aside', 'details', 'summary', 'figure', 'figcaption',
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'target', 'rel',
      'class', 'style', 'id', 'width', 'height',
      'colspan', 'rowspan', 'align', 'valign',
      'type', 'checked', 'disabled', // for task list checkboxes
      'data-type', 'data-checked', // Tiptap task items
      // #713 — referrerpolicy + loading must be in the allowlist so the
      // hook's setAttribute survives DOMPurify's attribute filter on
      // the next pass.
      'referrerpolicy', 'loading',
    ],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'], // Allow target="_blank" on links
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  });
}

/**
 * Sanitize an SVG document for safe rendering via dangerouslySetInnerHTML.
 *
 * `sanitizeHtml` above CANNOT be used for SVG: its explicit `ALLOWED_TAGS`
 * allowlist contains no SVG elements, so it would strip the entire <svg> tree
 * (e.g. a Mermaid diagram would render as an empty box). This uses DOMPurify's
 * built-in SVG profile, which permits SVG/SVG-filter elements while DOMPurify
 * still removes the XSS vectors it always strips (<script>, on* handlers,
 * javascript: URLs). We additionally forbid <foreignObject> — the standard
 * "HTML-inside-SVG" injection vector — as defence-in-depth (Mermaid with
 * securityLevel:'strict' emits <text> labels, not foreignObject HTML, so this
 * does not affect legitimate diagrams).
 */
export function sanitizeSvg(svg: string | null | undefined): string {
  if (!svg) return '';
  return DOMPurify.sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: ['script', 'foreignObject'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onanimationend'],
  });
}

/**
 * Sanitize a URL — block javascript:, data:text/html, and other dangerous schemes.
 * Safe for use in href and src attributes.
 */
export function sanitizeUrl(url: string | null | undefined): string {
  if (!url) return '';
  const trimmed = url.trim();
  // Block dangerous URL schemes
  if (/^(javascript|data|vbscript|blob):/i.test(trimmed)) return '';
  // Allow http, https, mailto, tel, and relative URLs
  if (/^(https?|mailto|tel|\/|#)/i.test(trimmed) || !trimmed.includes(':')) return trimmed;
  return '';
}
